// src/Utils/DateUtils.ts
import {
  adjustForTimezone,
  fromUTC,
  getDateHourZero,
  minMaxDates,
} from "./Time";

/**
 * Calcule les dates de début et de fin pour un semestre donné.
 */
export const getSemesterDates = (
  selectedYear: number,
  isFirstSemester: boolean,
  dateCloture: Date | null
): { startDate: Date; endDate: Date; maxDate: Date } => {
  const lastDayOfSem1 = new Date(selectedYear, 6, 1);
  const lastDayOfSem2 = new Date(selectedYear, 12, 1);
  const endDate = isFirstSemester ? lastDayOfSem1 : lastDayOfSem2;
  const startDate = isFirstSemester
    ? new Date(selectedYear, 0, 1)
    : new Date(selectedYear, 6, 1);

  if (dateCloture === null) {
    return {
      startDate,
      endDate,
      maxDate: endDate,
    };
  }

  const adjustedEndDate = adjustForTimezone(
    endDate < dateCloture ? endDate : dateCloture
  );
  const maxDate = endDate < dateCloture ? endDate : dateCloture;

  return {
    startDate,
    endDate: getDateHourZero(adjustedEndDate),
    maxDate: getDateHourZero(maxDate),
  };
};

/**
 * Calcule les dates de début et de fin pour un trimestre donné.
 */
export const getQuarterDates = (
  selectedYear: number,
  quarter: 1 | 2 | 3 | 4,
  dateCloture: Date | null
): { startDate: Date; endDate: Date; maxDate: Date } => {
  const quarterStartMonths = [0, 3, 6, 9];
  const quarterEndMonths = [3, 6, 9, 12];
  const startDate = new Date(selectedYear, quarterStartMonths[quarter - 1], 1);
  const endDate = new Date(selectedYear, quarterEndMonths[quarter - 1], 1);

  if (dateCloture === null) {
    return {
      startDate,
      endDate,
      maxDate: endDate,
    };
  }

  const adjustedEndDate = adjustForTimezone(
    endDate < dateCloture ? endDate : dateCloture
  );
  const maxDate = endDate < dateCloture ? endDate : dateCloture;

  return {
    startDate,
    endDate: getDateHourZero(adjustedEndDate),
    maxDate: getDateHourZero(maxDate),
  };
};

/**
 * Calcule les dates de début et de fin pour un mois donné.
 */
export const getMonthDates = (
  selectedYear: number,
  monthIndex: number,
  dateCloture: Date | null
): { startDate: Date; endDate: Date; maxDate: Date } => {
  const startDate = new Date(selectedYear, monthIndex, 1);
  const endDate = new Date(selectedYear, monthIndex + 1, 1);

  if (dateCloture === null) {
    return {
      startDate,
      endDate,
      maxDate: endDate,
    };
  }

  const adjustedEndDate = adjustForTimezone(
    endDate < dateCloture ? endDate : dateCloture
  );
  const maxDate = endDate < dateCloture ? endDate : dateCloture;

  return {
    startDate,
    endDate: getDateHourZero(adjustedEndDate),
    maxDate: getDateHourZero(maxDate),
  };
};

/**
 * Calcule les dates de début et de fin pour une année complète.
 */
export const getYearDates = (
  selectedYear: number,
  dateCloture: Date | null
): { startDate: Date; endDate: Date; maxDate: Date } => {
  const startDate = new Date(selectedYear, 0, 1);
  const endDate = new Date(selectedYear, 12, 1);

  if (dateCloture === null) {
    return {
      startDate,
      endDate,
      maxDate: endDate,
    };
  }

  const adjustedEndDate = adjustForTimezone(
    endDate < dateCloture ? endDate : dateCloture
  );
  const maxDate = endDate < dateCloture ? endDate : dateCloture;

  return {
    startDate,
    endDate: getDateHourZero(adjustedEndDate),
    maxDate: getDateHourZero(maxDate),
  };
};

/**
 * Calcule les dates de début et de fin pour une période personnalisée (élections).
 */
export const getElectionDates = (
  electionsStartDate: Date,
  electionsEndDate: Date,
  dateCloture: Date
): { startDate: Date; endDate: Date; maxDate: Date } => {
  const endDate = minMaxDates([electionsEndDate, dateCloture]).minDate;
  const startDate = minMaxDates([electionsStartDate, endDate]).minDate;
  return {
    startDate: adjustForTimezone(new Date(startDate)),
    endDate: getDateHourZero(fromUTC(endDate)),
    maxDate: endDate,
  };
};

/**
 * Ajuste une date de fin pour inclure toute la journée.
 * Si la date est à minuit, on la décale à 00:00:00.0000 du jour suivant.
 */
export const adjustEndDateForInclusion = (date: Date): Date => {
  let newDate = new Date(date);
  if (
    newDate.getHours() === 0 &&
    newDate.getMinutes() === 0 &&
    newDate.getSeconds() === 0
  ) {
    newDate = new Date(date.getTime() + 24 * 60 * 60 * 1000);
  }
  return newDate;
};
